import { ClientError } from 'graphql-request';
import type { ErrorCode } from './codes';

export function getErrorCode(error: unknown): ErrorCode | undefined {
  if (!(error instanceof ClientError)) return undefined;
  const code = error.response.errors?.[0]?.extensions?.code;
  return typeof code === 'string' ? (code as ErrorCode) : undefined;
}

export const DEFAULT_ERROR_MESSAGE = 'Une erreur est survenue.';

// U-03 : jamais le message brut de l'API à l'écran — uniquement des messages
// FR mappés par code. Un écran qui veut un wording plus fin le gère lui-même
// via getErrorCode.
const MESSAGES: Partial<Record<ErrorCode, string>> = {
  INVALID_CREDENTIALS: 'Identifiants incorrects.',
  ACCOUNT_LOCKED:
    'Compte temporairement verrouillé. Réessayez dans quelques minutes.',
  RATE_LIMITED: 'Trop de tentatives. Réessayez plus tard.',
  EMAIL_VERIFICATION_FAILED: 'Ce lien est invalide ou a expiré.',
  SIRET_ALREADY_USED: 'Ce numéro de SIRET est déjà rattaché à un compte.',
  EMAIL_ALREADY_USED: 'Un compte existe déjà avec cet email. Connectez-vous.',
  VALIDATION_ERROR: 'Certains champs sont invalides.',
  SERVICE_UNAVAILABLE: 'Service momentanément indisponible. Réessayez.',
  USER_ALREADY_MEMBER: 'Cette personne fait déjà partie de l’entreprise.',
  INVALID_INVITATION_ROLE: 'Ce rôle ne peut pas être attribué par invitation.',
  PLAN_SEATS_EXCEEDED: 'Votre forfait n’a plus de siège disponible.',
  INVITATION_NOT_FOUND: 'Cette invitation n’existe plus.',
  CANNOT_CHANGE_OWN_ROLE: 'Vous ne pouvez pas changer votre propre rôle.',
  CANNOT_DEACTIVATE_SELF: 'Vous ne pouvez pas vous désactiver vous-même.',
  LAST_ACTIVE_OWNER:
    'Impossible : c’est le dernier directeur actif de l’entreprise.',
  USER_NOT_FOUND: 'Ce membre est introuvable.',
  CANNOT_SUSPEND_SELF: 'Vous ne pouvez pas vous suspendre vous-même.',
  SUBSCRIPTION_INACTIVE:
    'Votre abonnement n’est pas actif. Choisissez un forfait pour continuer.',
  SUBSCRIPTION_ALREADY_ACTIVE: 'Vous avez déjà un abonnement actif.',
  INVALID_PLAN: 'Ce forfait n’est pas disponible.',
  JOURNAL_ENTRY_NOT_FOUND: 'Cette entrée n’existe plus.',
  WORKSITE_LOCKED:
    'Ce chantier est annulé : plus aucune modification n’est possible.',
  WORKSITE_COMPLETED:
    'Ce chantier est livré : rouvrez-le pour modifier le déroulé.',
  SHARE_NOT_ACTIVE:
    'Le partage n’est plus actif : réactivez-le pour envoyer le lien.',
  CLIENT_EMAIL_MISSING:
    'Ce client n’a pas d’adresse e-mail : ajoutez-la sur sa fiche.',
  STAGE_NOT_FOUND: 'Cette étape n’existe plus.',
  STAGE_LIMIT_REACHED: 'Ce chantier a atteint la limite de 100 étapes.',
  // Partagé entre chantier inexistant, cross-tenant et brouillon vu par un
  // USER (CH-30) : un seul code, aucun oracle d'existence.
  WORKSITE_NOT_FOUND: 'Ce chantier n’existe plus.',
  SUBCONTRACTOR_NOT_FOUND: 'Ce sous-traitant est introuvable.',
  SUBCONTRACTOR_ARCHIVED:
    'Ce sous-traitant est archivé : désarchivez-le pour l’affecter.',
  INVALID_STAGE_ORDER: 'L’ordre a changé entre-temps. La liste a été rechargée.',
  PROGRESS_DERIVED: 'L’avancement se calcule à partir des étapes.',
  ASSIGNMENT_NOT_FOUND: 'Cette affectation n’existe plus.',
  // Générique : le panel dit « personne » ou « entreprise » selon l'onglet.
  ASSIGNMENT_ALREADY_EXISTS: 'Cette affectation existe déjà.',
};

export function getErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE,
): string {
  const code = getErrorCode(error);
  return (code && MESSAGES[code]) ?? fallback;
}
